import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

const inputClass = "w-full rounded-xl border border-emerald-100 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100";
const labelClass = "text-xs font-extrabold uppercase tracking-[0.16em] text-slate-500";

function SubmitButton({ loading, children, testId }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-extrabold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
      data-testid={testId}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
      {children}
    </button>
  );
}

function SuccessBox({ message, testId }) {
  if (!message) return null;
  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold leading-6 text-emerald-800" data-testid={testId}>
      <CheckCircle2 className="mb-2 h-5 w-5" />
      {message}
    </div>
  );
}

function ConsentCheckbox({ checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600" data-testid={`${testId}-label`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-emerald-200 text-emerald-600"
        data-testid={testId}
      />
      <span>J’accepte d’être recontacté(e) par l’équipe Hana au sujet de cette demande.</span>
    </label>
  );
}

async function submitLead(path, payload) {
  const response = await api.post(path, payload, { skipAuth: true });
  return response.data;
}

export function PatientInterestForm({ compact = false }) {
  const [form, setForm] = useState({ fullName: "", phone: "", email: "", clinicName: "", doctorName: "", city: "", cancerType: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSuccess("");
    try {
      const payload = { ...form, email: form.email || null };
      const data = await submitLead("/public-leads/patient", payload);
      setSuccess(data.message);
      toast.success("Demande reçue");
      setForm({ fullName: "", phone: "", email: "", clinicName: "", doctorName: "", city: "", cancerType: "", message: "", consent: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d’envoyer la demande pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-sm" data-testid="patient-interest-form">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-teal-500">Demande patient</p>
      <h3 className="mt-3 text-2xl font-extrabold text-slate-800">Demander l’activation de mon accès</h3>
      <p className="mt-2 text-sm leading-6 text-slate-500">Indiquez la clinique et le médecin qui vous suivent : votre équipe activera votre accès.</p>
      <div className={`mt-6 grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
        <label><span className={labelClass}>Nom complet</span><input required className={inputClass} value={form.fullName} onChange={(e) => update("fullName", e.target.value)} data-testid="patient-form-full-name-input" /></label>
        <label><span className={labelClass}>Téléphone</span><input required className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="patient-form-phone-input" /></label>
        <label><span className={labelClass}>Email optionnel</span><input type="email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="patient-form-email-input" /></label>
        <label><span className={labelClass}>Ville</span><input className={inputClass} value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="patient-form-city-input" /></label>
        <label><span className={labelClass}>Clinique qui vous suit</span><input required className={inputClass} value={form.clinicName} onChange={(e) => update("clinicName", e.target.value)} data-testid="patient-form-clinic-input" /></label>
        <label><span className={labelClass}>Médecin référent</span><input required className={inputClass} value={form.doctorName} onChange={(e) => update("doctorName", e.target.value)} data-testid="patient-form-doctor-input" /></label>
        <label className="md:col-span-2"><span className={labelClass}>Type de cancer ou traitement optionnel</span><input className={inputClass} value={form.cancerType} onChange={(e) => update("cancerType", e.target.value)} data-testid="patient-form-cancer-type-input" /></label>
        <label className="md:col-span-2"><span className={labelClass}>Message optionnel</span><textarea rows={3} className={inputClass} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="patient-form-message-input" /></label>
      </div>
      <div className="mt-5"><ConsentCheckbox checked={form.consent} onChange={(v) => update("consent", v)} testId="patient-form-consent-checkbox" /></div>
      <div className="mt-5"><SubmitButton loading={loading} testId="patient-form-submit-button">Envoyer ma demande</SubmitButton></div>
      <div className="mt-5"><SuccessBox message={success} testId="patient-form-success-message" /></div>
    </form>
  );
}

export function ProfessionalContactForm() {
  const [form, setForm] = useState({ fullName: "", role: "", clinicName: "", city: "", email: "", phone: "", teamSize: "", priority: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSuccess("");
    try {
      const data = await submitLead("/public-leads/professional", form);
      setSuccess(data.message);
      toast.success("Demande professionnelle reçue");
      setForm({ fullName: "", role: "", clinicName: "", city: "", email: "", phone: "", teamSize: "", priority: "", message: "", consent: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d’envoyer la demande pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-sm" data-testid="professional-contact-form">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-teal-500">Démonstration</p>
      <h3 className="mt-3 text-2xl font-extrabold text-slate-800">Voir Hana avec votre équipe</h3>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <label><span className={labelClass}>Nom complet</span><input required className={inputClass} value={form.fullName} onChange={(e) => update("fullName", e.target.value)} data-testid="professional-form-full-name-input" /></label>
        <label><span className={labelClass}>Fonction</span><input required className={inputClass} value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="professional-form-role-input" /></label>
        <label><span className={labelClass}>Clinique / centre</span><input required className={inputClass} value={form.clinicName} onChange={(e) => update("clinicName", e.target.value)} data-testid="professional-form-clinic-input" /></label>
        <label><span className={labelClass}>Ville</span><input className={inputClass} value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="professional-form-city-input" /></label>
        <label><span className={labelClass}>Email professionnel</span><input required type="email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="professional-form-email-input" /></label>
        <label><span className={labelClass}>Téléphone optionnel</span><input className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="professional-form-phone-input" /></label>
        <label><span className={labelClass}>Volume patient approximatif</span><input className={inputClass} value={form.teamSize} onChange={(e) => update("teamSize", e.target.value)} data-testid="professional-form-team-size-input" /></label>
        <label><span className={labelClass}>Priorité</span><select className={inputClass} value={form.priority} onChange={(e) => update("priority", e.target.value)} data-testid="professional-form-priority-select"><option value="">Choisir</option><option>Bilans avant chimio</option><option>Suivi des symptômes</option><option>Traitement oral à domicile</option><option>Remplacer WhatsApp</option><option>Tout le suivi entre deux cures</option></select></label>
        <label className="md:col-span-2"><span className={labelClass}>Contexte</span><textarea rows={4} className={inputClass} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="professional-form-message-input" /></label>
      </div>
      <div className="mt-5"><ConsentCheckbox checked={form.consent} onChange={(v) => update("consent", v)} testId="professional-form-consent-checkbox" /></div>
      <div className="mt-5"><SubmitButton loading={loading} testId="professional-form-submit-button">Demander une démo</SubmitButton></div>
      <div className="mt-5"><SuccessBox message={success} testId="professional-form-success-message" /></div>
    </form>
  );
}

export function PharmaContactForm() {
  const [form, setForm] = useState({ fullName: "", company: "", role: "", email: "", phone: "", interest: "", indication: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSuccess("");
    try {
      const data = await submitLead("/public-leads/pharma", form);
      setSuccess(data.message);
      toast.success("Demande laboratoire reçue");
      setForm({ fullName: "", company: "", role: "", email: "", phone: "", interest: "", indication: "", message: "", consent: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d’envoyer la demande pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-sm" data-testid="pharma-contact-form">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-teal-500">Échange RWE</p>
      <h3 className="mt-3 text-2xl font-extrabold text-slate-800">Cadrer un projet avec votre laboratoire</h3>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <label><span className={labelClass}>Nom complet</span><input required className={inputClass} value={form.fullName} onChange={(e) => update("fullName", e.target.value)} data-testid="pharma-form-full-name-input" /></label>
        <label><span className={labelClass}>Laboratoire</span><input required className={inputClass} value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="pharma-form-company-input" /></label>
        <label><span className={labelClass}>Fonction</span><input required className={inputClass} value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="pharma-form-role-input" /></label>
        <label><span className={labelClass}>Email professionnel</span><input required type="email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="pharma-form-email-input" /></label>
        <label><span className={labelClass}>Téléphone optionnel</span><input className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="pharma-form-phone-input" /></label>
        <label><span className={labelClass}>Sujet prioritaire</span><select required className={inputClass} value={form.interest} onChange={(e) => update("interest", e.target.value)} data-testid="pharma-form-interest-select"><option value="">Choisir</option><option>Traitement oral à domicile</option><option>Toxicités / PRO</option><option>Parcours patient</option><option>Biomarqueurs / cohortes</option><option>Programme patient</option></select></label>
        <label className="md:col-span-2"><span className={labelClass}>Indication ou population concernée</span><input className={inputClass} value={form.indication} onChange={(e) => update("indication", e.target.value)} data-testid="pharma-form-indication-input" /></label>
        <label className="md:col-span-2"><span className={labelClass}>Message</span><textarea rows={4} className={inputClass} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="pharma-form-message-input" /></label>
      </div>
      <div className="mt-5"><ConsentCheckbox checked={form.consent} onChange={(v) => update("consent", v)} testId="pharma-form-consent-checkbox" /></div>
      <div className="mt-5"><SubmitButton loading={loading} testId="pharma-form-submit-button">Demander un échange</SubmitButton></div>
      <div className="mt-5"><SuccessBox message={success} testId="pharma-form-success-message" /></div>
    </form>
  );
}